/**
 * Recognising UI injected into the page by browser extensions, so a click on it
 * is not mistaken for a click "outside" one of our overlays.
 *
 * ## The problem
 *
 * Grammarly (and similar writing extensions) attach to `contenteditable` regions
 * and render their own UI — the floating "G" button, the suggestion card — into
 * elements they inject at `document.body` level, outside our React root. Our
 * dismissal logic asks "is the click target inside my panel?", the answer is no,
 * and the panel closes underneath the user mid-edit.
 *
 * ## Why we can identify it without piercing the shadow boundary
 *
 * That UI lives inside a shadow root, which at first looks like it puts the real
 * target out of reach. It does not: when an event crosses out of a shadow tree
 * the browser **retargets** it, so `event.target` as observed from outside is the
 * shadow *host* — the injected custom element itself. Matching the host is all we
 * need, and it is a plain `closest()` call.
 *
 * This is why the check works for **closed** shadow roots too. Retargeting happens
 * regardless of mode; the open/closed distinction only matters when looking *inward*
 * (cf. react-aria's own note in `useInteractOutside`: "This only works if the shadow
 * root is open, there is no way to detect if it is closed"). We look outward-in, at
 * the host, so mode is irrelevant.
 *
 * ## Precedent
 *
 * react-aria already does exactly this shape of check against `event.target` for
 * its own portalled surfaces (`event.target.closest('[data-react-aria-top-layer]')`),
 * as do our `[data-keep-overlay-open]` and `[data-trigger]` escape hatches. The only
 * difference here is that we key off vendor markup, because we cannot stamp an
 * attribute onto DOM we do not own.
 *
 * ## Maintenance
 *
 * The selector below is the fragile part — it is Grammarly's markup, not ours, and
 * it can change when they ship a new build. If overlays start closing on Grammarly
 * clicks again, verify with the extension active and update this one constant:
 *
 *     document.querySelectorAll('grammarly-extension, [data-grammarly-shadow-root]')
 */

/**
 * Elements injected by writing extensions. `grammarly-extension` is the overlay
 * that paints underlines over the editable; `grammarly-desktop-integration` hosts
 * the floating button and suggestion card. The `data-grammarly-shadow-root` and
 * `data-gramm_editor` attributes are stamped on hosts and attached editables
 * respectively.
 */
const THIRD_PARTY_EDITOR_UI_SELECTOR = [
  'grammarly-extension',
  'grammarly-desktop-integration',
  '[data-grammarly-shadow-root]',
  '[data-gramm_editor]',
].join(', ');

/**
 * Whether `target` is UI injected by a writing extension (currently Grammarly).
 *
 * Walks up through any nested shadow hosts, because `closest()` stops at a shadow
 * boundary — if the injected host is itself inside another shadow root, a plain
 * `closest()` from the retargeted node would miss it.
 */
export function isThirdPartyEditorUi(
  target: EventTarget | Node | null
): boolean {
  let node: Node | null = target instanceof Node ? target : null;

  while (node) {
    if (
      node instanceof Element &&
      node.closest(THIRD_PARTY_EDITOR_UI_SELECTOR) != null
    ) {
      return true;
    }
    // Step out of the current shadow tree, if any, and continue from its host.
    const root = node.getRootNode();
    node = root instanceof ShadowRoot ? root.host : null;
  }

  return false;
}

/**
 * Whether `target` has been detached from the document.
 *
 * Extensions routinely remove or reparent nodes between a pointer going down and
 * the handler running. A detached node is inside nothing, so a naive
 * `ref.contains(target)` check reports "outside" and dismisses the overlay.
 *
 * react-aria already guards this for overlays (`useInteractOutside` bails when
 * `documentElement.contains(event.target)` is false); this exists so the
 * hand-rolled `document` listeners elsewhere in the app can reach the same bar.
 */
export function isDetachedFromDocument(
  target: EventTarget | Node | null
): boolean {
  if (!(target instanceof Node)) {
    return false;
  }
  return !target.isConnected;
}

/**
 * Whether a click on `target` should be ignored by close-on-click-outside logic.
 *
 * Combines both guards above. Use this in any `document`-level `mousedown` /
 * `pointerdown` handler that would otherwise treat "not inside my ref" as
 * "dismiss me".
 */
export function shouldIgnoreOutsideInteraction(
  target: EventTarget | Node | null
): boolean {
  return isThirdPartyEditorUi(target) || isDetachedFromDocument(target);
}
